import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, CardActionArea, Chip, LinearProgress, Paper, Avatar, Fade, Button, Divider } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SchoolIcon from '@mui/icons-material/School';
import ExtensionIcon from '@mui/icons-material/Extension';
import VerifiedIcon from '@mui/icons-material/Verified';
import api from '../../services/api';
import { Parcours, Bloc } from '../../types/course';
import LottieLoading from '../../components/common/LottieLoading';
import { decodeHTMLEntities } from '../../utils/stringUtils';


const LearnerParcoursDetails = () => {
    const { id } = useParams<{ id: string }>();
    const [parcours, setParcours] = useState<Parcours | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const fetchDetails = async () => {
        try {
            const res = await api.get('/api/course/parcours');
            const found = res.data.find((p: Parcours) => p._id === id);
            if (found && found.blocs) {
                // Compute total courses per block
                const blocsWithCounts = found.blocs.map((bloc: Bloc) => {
                    const countCourses = (cat: any): number => {
                        let count = 0;
                        if (cat.courses) count += cat.courses.length;
                        if (cat.categories) {
                            cat.categories.forEach((sub: any) => {
                                count += countCourses(sub);
                            });
                        }
                        return count;
                    };
                    const totalCoursesCount = bloc.categories ? bloc.categories.reduce((acc: number, cat: any) => acc + countCourses(cat), 0) : 0;
                    const completedCoursesCount = bloc.completedCoursesCount ?? 0;
                    return { ...bloc, totalCoursesCount, completedCoursesCount };
                });
                found.blocs = blocsWithCounts;
            }
            setParcours(found);
        } catch (err) {
            console.error("Error fetching details", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LottieLoading message="Chargement du parcours..." animationType="learner" />;
    if (!parcours) return (
        <Box sx={{ textAlign: 'center', mt: 10, p: 4 }}>
            <Typography variant="h5" color="error">Parcours introuvable</Typography>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/learner/parcours')} sx={{ mt: 2 }}>
                Retour aux parcours
            </Button>
        </Box>
    );

    // Calculate overall progress
    const totalCourses = parcours.blocs?.reduce((acc, b: any) => acc + (b.totalCoursesCount || 0), 0) || 0;
    const completedCourses = parcours.blocs?.reduce((acc, b: any) => acc + (b.completedCoursesCount || 0), 0) || 0;
    const progress = totalCourses > 0 ? (completedCourses / totalCourses) * 100 : 0;

    return (
        <Box sx={{
            minHeight: '100vh',
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 1, sm: 2, md: 4 },
            overflowX: 'hidden'
        }}>
            <Box sx={{ mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/learner/parcours')}
                    sx={{ color: '#667eea', fontWeight: 600, textTransform: 'none', '&:hover': { background: 'transparent', opacity: 0.8 } }}
                >
                    Retour à mes parcours
                </Button>
            </Box>

            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, sm: 4, md: 5 },
                    mb: { xs: 3, md: 5 },
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                }}
            >
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', filter: 'blur(30px)' }} />
                <Box sx={{ position: 'absolute', bottom: -30, left: 40, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />

                <Grid container spacing={{ xs: 1, md: 4 }} alignItems="center">
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'center', sm: 'flex-start' },
                            textAlign: { xs: 'center', sm: 'left' },
                            gap: 2,
                            mb: 2
                        }}>
                            <Avatar sx={{
                                width: { xs: 56, md: 64 },
                                height: { xs: 56, md: 64 },
                                bgcolor: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)'
                            }}>
                                <SchoolIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                            </Avatar>
                            <Box>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, opacity: 0.8, fontWeight: 600, display: 'block' }}>
                                    Parcours de Formation
                                </Typography>
                                <Typography variant="h3" sx={{
                                    fontWeight: 800,
                                    fontFamily: 'Quicksand, sans-serif',
                                    fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' },
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere'
                                }}>
                                    {decodeHTMLEntities(parcours.title)}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography variant="body1" sx={{
                            opacity: 0.9,
                            maxWidth: 800,
                            fontFamily: 'Quicksand, sans-serif',
                            fontSize: { xs: '0.95rem', md: '1.1rem' },
                            lineHeight: 1.6,
                            textAlign: { xs: 'center', sm: 'left' },
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            mb: 3,
                            display: { xs: 'none', md: 'block' }
                        }}>
                            {parcours.description}
                        </Typography>

                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            mt: 3,
                            flexWrap: 'wrap',
                            justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}>
                            <Chip
                                icon={<ExtensionIcon sx={{ color: 'white !important' }} />}
                                label={`${parcours.blocs?.length || 0} Blocs`}
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', backdropFilter: 'blur(5px)', border: '1px solid rgba(255,255,255,0.3)' }}
                            />
                            {progress >= 100 && (
                                <Chip
                                    icon={<VerifiedIcon sx={{ color: '#4caf50 !important' }} />}
                                    label="Terminé"
                                    sx={{ bgcolor: 'white', color: '#2e7d32', fontWeight: 700 }}
                                />
                            )}
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                        <Box sx={{
                            bgcolor: 'rgba(255,255,255,0.15)',
                            backdropFilter: 'blur(10px)',
                            p: { xs: 2, sm: 3 },
                            borderRadius: 3,
                            mt: { xs: 2, md: 0 }
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', md: '1rem' } }}>Progression globale</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>{Math.round(progress)}%</Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: { xs: 8, md: 12 },
                                    borderRadius: 6,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 6,
                                        background: progress === 100 ? '#4caf50' : 'white'
                                    }
                                }}
                            />
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8, textAlign: 'right', fontWeight: 600 }}>
                                {completedCourses}/{totalCourses} cours terminés
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif', color: '#2c3e50', mb: 3 }}>
                Contenu du parcours
            </Typography>

            <Grid container spacing={{ xs: 1, md: 4 }}>
                {parcours.blocs?.map((bloc: any, index: number) => (
                    <Grid size={{ xs: 12, md: 6 }} key={bloc._id}>
                        <Fade in timeout={500 + index * 100}>
                            <Card sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 4,
                                border: '1px solid white',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                overflow: 'visible',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.15)',
                                }
                            }}>
                                <CardActionArea
                                    onClick={() => navigate(`/learner/parcours/${parcours._id}/bloc/${bloc._id}`)}
                                    sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', borderRadius: 4 }}
                                >
                                    <Box sx={{
                                        p: 3,
                                        background: 'linear-gradient(to right, #ffffff, #f9fafb)',
                                        borderBottom: '1px solid rgba(0,0,0,0.05)'
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{
                                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                color: '#667eea',
                                                width: 50,
                                                height: 50,
                                                mr: 2
                                            }}>
                                                <FolderIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h6" sx={{
                                                    fontWeight: 700,
                                                    color: '#2c3e50',
                                                    fontFamily: 'Quicksand, sans-serif',
                                                    lineHeight: 1.2,
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'anywhere'
                                                }}>
                                                    {decodeHTMLEntities(bloc.title)}
                                                </Typography>
                                                {bloc.certificationNumber && (
                                                    <Chip
                                                        label={`Certif: ${bloc.certificationNumber}`}
                                                        size="small"
                                                        sx={{ mt: 0.5, height: 20, fontSize: '0.7rem', bgcolor: '#f0f0f0' }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        <Box sx={{ mt: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Avancement</Typography>
                                                <Typography variant="caption" color="primary" sx={{ fontWeight: 700 }}>
                                                    {bloc.totalCoursesCount > 0
                                                        ? Math.round((bloc.completedCoursesCount / bloc.totalCoursesCount) * 100)
                                                        : 0}%
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={bloc.totalCoursesCount > 0 ? (bloc.completedCoursesCount / bloc.totalCoursesCount) * 100 : 0}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: 4,
                                                    bgcolor: '#f0f0f0',
                                                    '& .MuiLinearProgress-bar': {
                                                        borderRadius: 4,
                                                        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <CardContent sx={{ flexGrow: 1, p: 3, bgcolor: 'white' }}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 6 }}>
                                                <Box sx={{ p: 1.5, bgcolor: '#f5f7fa', borderRadius: 2, textAlign: 'center' }}>
                                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#667eea' }}>
                                                        {bloc.categories?.length || 0}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                        Catégories
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6 }}>
                                                <Box sx={{ p: 1.5, bgcolor: '#f5f7fa', borderRadius: 2, textAlign: 'center' }}>
                                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#667eea' }}>
                                                        {bloc.evaluationsCount || 0}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                        Évaluations
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        {bloc.competencies && bloc.competencies.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}>
                                                    Compétences clés :
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {bloc.competencies.slice(0, 3).map((comp: string, i: number) => (
                                                        <Chip key={i} label={comp} size="small" sx={{ fontSize: '0.7rem', height: 24 }} />
                                                    ))}
                                                    {bloc.competencies.length > 3 && (
                                                        <Chip label={`+${bloc.competencies.length - 3}`} size="small" sx={{ fontSize: '0.7rem', height: 24 }} />
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>

                                    <Divider />
                                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', bgcolor: '#fafafa' }}>
                                        <Typography variant="button" sx={{ color: '#667eea', fontWeight: 700, fontSize: '0.85rem' }}>
                                            Accéder au contenu
                                        </Typography>
                                    </Box>
                                </CardActionArea>
                            </Card>
                        </Fade>
                    </Grid>
                ))}

                {(!parcours.blocs || parcours.blocs.length === 0) && (
                    <Grid size={{ xs: 12 }}>
                        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '2px dashed #e0e0e0' }} elevation={0}>
                            <Box sx={{ color: '#ccc', mb: 2 }}>
                                <ExtensionIcon sx={{ fontSize: 48 }} />
                            </Box>
                            <Typography variant="h6" color="text.secondary">
                                Aucun bloc de compétences disponible pour le moment.
                            </Typography>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default LearnerParcoursDetails;
